import React from 'react';
import moment, { Moment } from 'moment';
import { DEFAULT_NS } from 'appConstants/translationNamespaces';
import { useTranslation } from 'react-i18next';
import { FlattenInterpolation } from 'styled-components';
import { formatString } from 'services/utils';
import { DateRangePickerBasic, CustomRangeInterface, DatePickerBasisProps } from 'shared-components/DatePickerBasic';
import { LocationFor } from 'components/AdvancedAnalyticsEvaluate/types';
import { NextBtn, PrevBtn } from './components';
import { OnCancelPropTypes, DateChangeHandlerPropTypes } from '../DatePickerBasic';

export interface DateRange {
  startDate: Moment;
  endDate: Moment;
  endPredictiveDate?: Moment | null;
}

interface PropTypes {
  appliedStartDate: Moment;
  appliedEndDate: Moment;
  onApply: (startDate: Moment, endDate: Moment, rangeName: string) => void;
  onCancel?: () => void;
  labelColor?: string;
  monthFormat?: string;
  isTablet?: boolean;
  isMobile?: boolean;
  windowWidth?: number;
  hideChevron?: boolean;
  appliedEndPredictiveDate?: Moment | null;
  moveArrowLeftOnTablet?: boolean;
  isRightSide?: boolean;
  additionalStyles?: FlattenInterpolation<any>;
  locationFor?: LocationFor;
  is30DaysPredictionData: boolean;
}

type TranslationKeys =
  | 'yesterday'
  | 'last7Days'
  | 'last30Days'
  | 'thisMonth'
  | 'lastMonth'
  | 'thru'
  | 'applyBtnText'
  | 'cancelBtnText'
  | 'customRange'
  | 'predictiveDayTooltip'
  | 'predictive30DaysTooltip';

type DateRangePickerTranslations = {
  [key in TranslationKeys]: string;
};

const createRanges = (labels: DateRangePickerTranslations): CustomRangeInterface => {
  const createRange = (startDate: Moment, endDate: Moment) => ({ startDate, endDate });
  const ranges: CustomRangeInterface = {};
  ranges[labels.yesterday] = createRange(moment().subtract(1, 'days'), moment().subtract(1, 'days'));
  ranges[labels.last7Days] = createRange(moment().subtract(6, 'days'), moment());
  ranges[labels.last30Days] = createRange(moment().subtract(29, 'days'), moment());
  ranges[labels.thisMonth] = createRange(moment().startOf('month'), moment().endOf('month'));
  ranges[labels.lastMonth] = createRange(
    moment().subtract(1, 'month').startOf('month'),
    moment().subtract(1, 'month').endOf('month')
  );
  return ranges;
};

export const DateRangePicker = (props: PropTypes) => {
  const {
    appliedEndDate,
    appliedStartDate,
    appliedEndPredictiveDate,
    onApply: onApplyHandler,
    isMobile,
    windowWidth,
    labelColor,
    isTablet,
    moveArrowLeftOnTablet,
    isRightSide,
    additionalStyles,
    locationFor,
    is30DaysPredictionData
  } = props;
  const { t } = useTranslation(DEFAULT_NS);
  const translations: DateRangePickerTranslations = {
    yesterday: t('rangeYesterday', 'Yesterday'),
    last7Days: `${formatString(t('rangeLastDays', 'Last {0} Days'), '7').join('')}`,
    last30Days: `${formatString(t('rangeLastDays', 'Last {0} Days'), '30').join('')}`,
    thisMonth: t('rangeThisMonth', 'This Month'),
    lastMonth: t('rangeLastMonth', 'Last Month'),
    thru: t('thru', 'thru'),
    customRange: t('customRange', 'Custom Range'),
    applyBtnText: t('apply', 'Apply'),
    cancelBtnText: t('labelCancel', 'Cancel'),
    predictiveDayTooltip: t('predictiveDayTooltip', '<b>Forecast Dates</b> show up to 7-days in <br/> the future'),
    predictive30DaysTooltip: t(
      'predictive30DaysTooltip',
      '<b>Forecast Dates</b> show up to 30-days in <br/> the future.'
    )
  };
  const ranges = createRanges(translations);

  const checkIsOutsideRange: DatePickerBasisProps['checkIsOutsideRange'] =
    (startDate, endDate, isEndDateSelected, isPredictionDataEnabled) => day =>
      isPredictionDataEnabled && day.isAfter(appliedEndPredictiveDate as Moment, 'day');

  const onCancel = (onCalcelProps: OnCancelPropTypes): void => {
    onCalcelProps.setInputFocus(null);
    onCalcelProps.setSelectedRange(undefined);
  };

  const dateChangeHandler = (dateChangeHandlerProps: DateChangeHandlerPropTypes): void => {
    const { setStartDate, nextStartDate, nextEndDate, setEndDate, startDate, setSelectedRange, setInputFocus } =
      dateChangeHandlerProps;
    setStartDate(nextStartDate);
    setEndDate(nextStartDate && nextEndDate && nextStartDate !== startDate ? null : nextEndDate);
    setSelectedRange(undefined);
    if (nextStartDate && nextEndDate) setInputFocus('startDate');
  };

  return (
    <DateRangePickerBasic
      checkIsOutsideRange={checkIsOutsideRange}
      labelColor={labelColor}
      appliedEndDate={appliedEndDate}
      appliedStartDate={appliedStartDate}
      onCancel={onCancel}
      dateChangeHandler={dateChangeHandler}
      onApply={onApplyHandler}
      ranges={ranges}
      isMobile={isMobile}
      windowWidth={windowWidth}
      translations={translations}
      nextBtn={NextBtn}
      prevBtn={PrevBtn}
      appliedEndPredictiveDate={appliedEndPredictiveDate}
      isTablet={isTablet}
      moveArrowLeftOnTablet={moveArrowLeftOnTablet}
      isRightSide={isRightSide}
      additionalStyles={additionalStyles}
      locationFor={locationFor}
      is30DaysPredictionData={is30DaysPredictionData}
    />
  );
};
